import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { scenarios } from './data';

// Visitors "earn rating" as they explore: sections visited and puzzles solved.
// Everything is stored locally in the visitor's browser.

const STORAGE_KEY = 'rr-chess-progress-v1';
export const SECTIONS = ['opening', 'moves', 'puzzles', 'endgame'];
const VISIT_MESSAGES = {
  opening: 'Opening studied',
  moves: 'Game reviewed',
  puzzles: 'Puzzle room entered',
  endgame: 'Endgame reached',
};
const SECTION_POINTS = 50;
const PUZZLE_POINTS = 100;
// Grandmaster (2500) is reached exactly when every section and puzzle is done.
export const MAX_RATING = 2500;
export const BASE_RATING = MAX_RATING - SECTIONS.length * SECTION_POINTS - scenarios.length * PUZZLE_POINTS;

const TITLES = [
  { min: 2500, name: 'Grandmaster', short: 'GM' },
  { min: 2400, name: 'International Master', short: 'IM' },
  { min: 2300, name: 'FIDE Master', short: 'FM' },
  { min: 2200, name: 'Candidate Master', short: 'CM' },
  { min: 2000, name: 'Expert', short: 'EX' },
  { min: 1800, name: 'Class A', short: 'A' },
  { min: 0, name: 'Club Player', short: 'CP' },
];

export const titleFor = (rating) => TITLES.find((t) => rating >= t.min);
export const nextTitleFor = (rating) => [...TITLES].reverse().find((t) => t.min > rating);

function load() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function save(done) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(done));
  } catch {
    // Storage can be unavailable (private mode); progress just won't persist.
  }
}

function ratingOf(done) {
  return Object.keys(done).reduce((sum, key) => {
    if (key.startsWith('visit:')) return sum + SECTION_POINTS;
    if (key.startsWith('puzzle:')) return sum + PUZZLE_POINTS;
    return sum;
  }, BASE_RATING);
}

const ProgressContext = createContext(null);

export function ProgressProvider({ children }) {
  const [done, setDone] = useState(load);
  const doneRef = useRef(done);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = useCallback((next) => {
    setToast(next);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3200);
  }, []);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const award = useCallback(
    (key, value, message) => {
      if (doneRef.current[key]) return;
      const before = ratingOf(doneRef.current);
      const next = { ...doneRef.current, [key]: value };
      doneRef.current = next;
      setDone(next);
      save(next);
      const after = ratingOf(next);
      const promoted = titleFor(after).name !== titleFor(before).name;
      showToast({
        id: Date.now(),
        points: after - before,
        message,
        title: promoted ? titleFor(after).name : null,
      });
    },
    [showToast]
  );

  const visit = useCallback((section) => award(`visit:${section}`, true, VISIT_MESSAGES[section]), [award]);

  const completePuzzle = useCallback(
    (id, how) => award(`puzzle:${id}`, how, how === 'solved' ? 'Puzzle solved' : 'Puzzle revealed'),
    [award]
  );

  const reset = useCallback(() => {
    doneRef.current = {};
    setDone({});
    save({});
  }, []);

  const value = useMemo(() => {
    const rating = ratingOf(done);
    return {
      done,
      rating,
      title: titleFor(rating),
      nextTitle: nextTitleFor(rating),
      sectionsVisited: SECTIONS.filter((s) => done[`visit:${s}`]).length,
      puzzlesDone: scenarios.filter((s) => done[`puzzle:${s.id}`]).length,
      puzzleState: (id) => done[`puzzle:${id}`] || null,
      visit,
      completePuzzle,
      reset,
      toast,
    };
  }, [done, visit, completePuzzle, reset, toast]);

  return <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>;
}

export const useProgress = () => useContext(ProgressContext);

// Marks a section as explored the first time a page mounts.
export function useVisit(section) {
  const { visit } = useProgress();
  useEffect(() => {
    visit(section);
  }, [section, visit]);
}
